import { z } from 'zod';
import type { FeeAuthorization } from '~/features/employer/billing/types';
import { getData, patchData, postData } from '~/utils/service';
import {
  ProviderJobMessageSchema,
  ProviderJobNegotiationChainRowSchema,
  ProviderJobResponseSchema,
} from './schemas';
import type {
  ProviderJobMessage,
  ProviderJobNegotiationChainRow,
  ProviderJobResponse,
  ProviderJobResponseFilter,
} from './types';

const ProviderJobResponseListResponseSchema = z.union([
  z.array(ProviderJobResponseSchema),
  z.object({ items: z.array(ProviderJobResponseSchema) }),
]);

const ViewedResponseSchema = z
  .object({
    viewedAt: z.string().nullable().optional(),
  })
  .passthrough();

const unwrapProviderJobResponses = (data: unknown): ProviderJobResponse[] => {
  const parsed = ProviderJobResponseListResponseSchema.parse(data);
  return Array.isArray(parsed) ? parsed : parsed.items;
};

export const API_CALLS = {
  getListingResponses: async ({
    filter = 'all',
    listingId,
    includeDeclined = false,
    signal,
  }: {
    filter?: ProviderJobResponseFilter;
    listingId?: number;
    includeDeclined?: boolean;
    signal?: AbortSignal;
  } = {}): Promise<ProviderJobResponse[]> => {
    const data = await getData('providerJobResponses', {
      signal,
      params: {
        status: filter,
        listingId: listingId?.toString(),
        // The API only offers `accept_listing` to callers that ask for it, so a bundle built
        // before that action existed (an open tab, or the minutes between the API and client
        // deploys) never receives a value its strict action enum would reject.
        acceptListing: 'true',
        // Same opt-in for `reject` (MCV-1850): the listing owner may decline in any open state.
        allowReject: 'true',
        // Declined and withdrawn responses only come back when asked for.
        includeDeclined: String(includeDeclined),
      },
    });
    return unwrapProviderJobResponses(data);
  },

  markViewed: async (responseId: number, signal?: AbortSignal): Promise<string | null> => {
    const data = await postData('jobResponseViewed', {
      signal,
      pathParams: { responseId: responseId.toString() },
    });
    return ViewedResponseSchema.parse(data).viewedAt ?? null;
  },

  getMessages: async (responseId: number, signal?: AbortSignal): Promise<ProviderJobMessage[]> => {
    const data = await getData('jobMessages', {
      signal,
      pathParams: { responseId: responseId.toString() },
    });
    return z
      .array(ProviderJobMessageSchema.refine((message) => message.responseId === responseId))
      .parse(data);
  },

  sendMessage: async (
    responseId: number,
    messageBody: string,
    signal?: AbortSignal
  ): Promise<ProviderJobMessage> => {
    const data = await postData('jobMessages', {
      signal,
      pathParams: { responseId: responseId.toString() },
      data: { messageBody },
    });
    return ProviderJobMessageSchema.refine((message) => message.responseId === responseId).parse(
      data
    );
  },

  acceptResponse: async (
    responseId: number,
    expectedNegotiationId: number | undefined,
    signal?: AbortSignal,
    // [MCV-1933] The paying employer's authorization of the fee schedule they were shown.
    feeAuthorization?: FeeAuthorization
  ): Promise<void> => {
    await postData('hlxAccept', {
      signal,
      pathParams: { responseId: responseId.toString() },
      data: { expectedNegotiationId, ...(feeAuthorization ? { feeAuthorization } : {}) },
    });
  },

  acceptListing: async (
    responseId: number,
    signal?: AbortSignal,
    feeAuthorization?: FeeAuthorization
  ): Promise<void> => {
    await postData('hlxAcceptListing', {
      signal,
      pathParams: { responseId: responseId.toString() },
      // [MCV-1933] Sent only when the preview carried a fee schedule; the body is otherwise empty.
      ...(feeAuthorization ? { data: { feeAuthorization } } : {}),
    });
  },

  rejectResponse: async (responseId: number, signal?: AbortSignal): Promise<void> => {
    await patchData('jobOpportunityStatus', {
      signal,
      pathParams: { responseId: responseId.toString() },
      data: { status: 'REJECTED' },
    });
  },

  withdrawResponse: async (responseId: number, signal?: AbortSignal): Promise<void> => {
    await patchData('jobOpportunityStatus', {
      signal,
      pathParams: { responseId: responseId.toString() },
      data: { status: 'WITHDRAWN' },
    });
  },

  getNegotiationChain: async (
    responseId: number,
    signal?: AbortSignal
  ): Promise<ProviderJobNegotiationChainRow[]> => {
    const data = await getData('jobNegotiationByResponse', {
      signal,
      pathParams: { responseId: responseId.toString() },
    });
    return z.array(ProviderJobNegotiationChainRowSchema).parse(data);
  },
};
